import { zodResolver } from "@hookform/resolvers/zod";
import type { JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { loginSchema, type LoginFormValues } from "../lib/login-schema";

interface LoginFormProps {
  onSubmit: (values: LoginFormValues) => void;
  isLoading: boolean;
  serverError?: string;
}

export function LoginForm({ onSubmit, isLoading, serverError }: LoginFormProps): JSX.Element {
  const { t } = useTranslation("auth");
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(data))} noValidate data-testid="login-form">
      <div className="space-y-4">
        <div>
          <label
            htmlFor="login-email"
            className="block text-sm font-medium text-[var(--text-primary)]"
          >
            {t("field.email")}
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            disabled={isLoading}
            data-testid="login-email"
            className="mt-1 w-full rounded border border-[var(--border-default)] px-3 py-2 text-sm outline-none focus:border-[var(--border-focus)] disabled:opacity-50"
            {...register("email")}
          />
          {errors.email && (
            <p
              className="mt-1 text-xs text-[var(--intent-danger-fg)]"
              data-testid="login-email-error"
            >
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="login-password"
            className="block text-sm font-medium text-[var(--text-primary)]"
          >
            {t("field.password")}
          </label>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            disabled={isLoading}
            data-testid="login-password"
            className="mt-1 w-full rounded border border-[var(--border-default)] px-3 py-2 text-sm outline-none focus:border-[var(--border-focus)] disabled:opacity-50"
            {...register("password")}
          />
          {errors.password && (
            <p
              className="mt-1 text-xs text-[var(--intent-danger-fg)]"
              data-testid="login-password-error"
            >
              {errors.password.message}
            </p>
          )}
        </div>

        {serverError && (
          <p className="text-sm text-[var(--intent-danger-fg)]" data-testid="login-server-error">
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={isLoading}
          data-testid="login-submit"
          className="w-full rounded bg-[var(--brand-500)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--brand-600)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? "…" : t("action.sign_in")}
        </button>
      </div>
    </form>
  );
}
